// Parish-timezone helpers (refactor PR A). Worker payloads carry
// startsAt as real UTC instants plus a `timezone` field (the parish's
// IANA zone, parishes.timezone, validated worker-side). All Mass-time
// formatting must use that zone — the browser's own zone belongs to
// the viewer, not the parish.

// The timezone to format a payload's timestamps in. A payload WITHOUT
// the field predates refactor PR A: its timestamps are MSP wall-clock
// times behind a fake Z suffix, and formatting those with
// timeZone:"UTC" reads the wall clock back out — exactly what the
// deleted UTC workarounds did, so old payloads render as before.
// (Both skew sources serve such payloads: the window between the
// Pages auto-deploy and the manual worker deploy, and KV-cached
// public schedules written pre-deploy.) Never fall back to a real
// zone here — that would shift fake-Z wall-clock times by the zone's
// offset.
export function payloadTimeZone(timezone: string | undefined): string {
  return timezone ?? "UTC";
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  weekday: number; // 0=Sunday ... 6=Saturday
  hour: number; // 0-23
  minute: number;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Intl.DateTimeFormat construction is expensive and these helpers run
// in per-row render loops (calendar bucketing, schedule grouping) —
// cache one formatter per timezone. An unrecognized zone name falls
// back to UTC instead of throwing out of a component render.
const dtfCache = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = dtfCache.get(timeZone);
  if (!f) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    try {
      f = new Intl.DateTimeFormat("en-US", { ...options, timeZone });
    } catch {
      f = new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" });
    }
    dtfCache.set(timeZone, f);
  }
  return f;
}

// Reads the wall-clock components an instant shows in `timeZone`.
// One formatToParts call feeds day-grouping keys, weekend checks, and
// month buckets without each caller re-deriving them. An invalid date
// degrades to NaN parts (all comparisons read false) instead of the
// RangeError formatToParts would throw mid-render.
export function zonedParts(d: Date, timeZone: string): ZonedParts {
  if (Number.isNaN(d.getTime())) {
    return { year: NaN, month: NaN, day: NaN, weekday: NaN, hour: NaN, minute: NaN };
  }
  const parts = partsFormatter(timeZone).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const num = (type: string) => Number(get(type) || "0");
  return {
    year: num("year"),
    month: num("month"),
    day: num("day"),
    weekday: Math.max(0, WEEKDAYS.indexOf(get("weekday"))),
    // Some engines render midnight as "24" with hour12:false.
    hour: num("hour") % 24,
    minute: num("minute"),
  };
}

// "YYYY-MM-DD" of the instant's parish-local calendar day.
export function zonedDayKey(d: Date, timeZone: string): string {
  const p = zonedParts(d, timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

// "YYYY-MM" of the instant's parish-local month.
export function zonedMonthKey(d: Date, timeZone: string): string {
  return zonedDayKey(d, timeZone).slice(0, 7);
}
